import I18n from "i18n-js";
import React from "react";
import { connect } from "react-redux";
import moment from "moment";
import { Link } from "react-router-dom";
import DownloadButton from "../../common/components/elements/DownloadButton";
import Dropdown from "../../common/components/elements/Dropdown";
import StatelessCheckbox from "../../common/components/form/StatelessCheckbox";
import { getLang } from "../../common/utils/langUtils";
import { getClientsList } from "../../company/actions/clientsActions";
import ClientUtils from "../../company/utils/ClientUtils";
import { getUsersList } from "../../users/actions/usersActions";
import DeleteRequestAction from "../actions/DeleteRequestAction";
import {
  getInterventionList,
  getInterventionListCount,
  getInterventionListFilters,
} from "../actions/interventionActions";
import InterventionList from "../components/InterventionList";
import InterventionType from "../models/InterventionType";
import Purpose from "../models/Purpose";
import pathToRegexp from "path-to-regexp";
import { ROUTE_INTERVENTION } from "../../../routes";
import { fetch } from "../../../appContainer";
import { TableWithTrackdechetsErrors } from "../../trackdechets/components/TableWithTrackdechetsErrors";

class InterventionListAll extends InterventionList {
  constructor(props) {
    super(props);

    this.interventionRoute = pathToRegexp.compile(ROUTE_INTERVENTION);
    this.exportToTsv = this.exportToTsv.bind(this);
  }

  exportToTsv() {
    const { filters } = this.state;

    return new Promise((download) => {
      fetch("api").exportInterventions(
        filters,
        (tsvContent) => download(tsvContent),
        (error) => {
          console.error(error);
          download(null);
        },
      );
    });
  }

  renderActions() {
    return (
      <DownloadButton
        fileContent={this.exportToTsv}
        filename={`climapp_intervention_${moment().format("YYYY-MM-DD_HH-mm-ss")}.tsv`}
        label={I18n.t("common.actions.export")}
        contentType="text/csv;charset=utf8;"
      />
    );
  }

  renderFilters() {
    const filters = super.renderFilters();
    const { userProfile } = this.props;

    if (userProfile.usesTrackdechets) {
      filters.push(
        <StatelessCheckbox
          key="filterWithTrackdechetsErrors"
          id="filterWithTrackdechetsErrors"
          label={I18n.t("filters.with_trackdechets_errors")}
          checked={Boolean(this.state.filters.trackdechetsError)}
          onChangedValue={(checked) =>
            this.onChangeCheckboxFilter(checked, "trackdechetsError")
          }
          className="mt-4"
        />,
      );
    }

    return filters;
  }

  dataTableHead() {
    return [
      I18n.t("pages.interventions.list.head.record"),
      I18n.t("pages.interventions.list.head.group"),
      I18n.t("pages.interventions.list.head.date"),
      ...(this.shouldRenderTechFilter
        ? [I18n.t("pages.interventions.list.head.technician")]
        : []),
      I18n.t("pages.interventions.list.head.client"),
      I18n.t("pages.interventions.list.head.installation"),
      I18n.t("pages.interventions.list.head.type"),
      I18n.t("pages.interventions.list.head.purpose"),
      I18n.t("pages.interventions.list.head.document"),
      "",
    ];
  }

  dataTableRow(rowData) {
    const {
      record,
      client,
      installation,
      creation,
      type,
      purpose,
      userUuid,
      documents,
    } = rowData;

    const actions = (
      <div>
        <Link
          to={this.interventionRoute({ uuid: rowData.uuid })}
          className="action"
        >
          <i className="fa-solid fa-eye" aria-hidden="true" />
        </Link>
        <DeleteRequestAction
          uuid={rowData.uuid}
          onDelete={this.removeIntervention}
        />
      </div>
    );

    const documentElements = documents?.map((document, index) => {
      return (
        <a href={document.url} target="blank" key={index}>
          {I18n.t(`pages.interventions.list.document.${document["type"]}`)}
        </a>
      );
    });

    const technician = this.renderTechnician(userUuid);

    return [
      <Link to={this.interventionRoute({ uuid: rowData.uuid })} key="record">
        {record}
      </Link>,
      rowData.groupNumber ?? '-',
      new Date(creation).toLocaleDateString(getLang()),
      ...(technician ? [technician] : []),
      ClientUtils.renderLabel(client),
      this.getInstallationLabel(installation),
      I18n.t(InterventionType.readableFor(type)),
      purpose ? I18n.t(Purpose.readableFor(purpose)) : null,
      documents && (
        <Dropdown
          label={
            documentElements.length +
            " " +
            I18n.t("pages.interventions.list.head.document")
          }
        >
          {documentElements}
        </Dropdown>
      ),
      actions,
    ];
  }

  /**
   * {@inheritdoc}
   */
  renderContent() {
    const { interventions, interventionsList, users, count, countInProgress, filtersInProgress } = this.props;

    if (interventions.error || (this.shouldRenderTechFilter && users.error)) {
      return <p>{I18n.t("common.loadingApiError")}</p>;
    }

    return (
      <TableWithTrackdechetsErrors
        target="INTERVENTION"
        showTrackdechetsErrorsByDefault
        showTrackdechetsToggle={false}
        allData={interventionsList || []}
        emptyContent={I18n.t("pages.interventions.list.empty")}
        data={this.getFilteredList()}
        dataCount={count}
        inProgress={interventions.inProgress || (this.shouldRenderTechFilter && users.inProgress) }
        countInProgress={countInProgress}
        filtersInProgress={filtersInProgress}
        headData={this.dataTableHead()}
        rowData={this.dataTableRow}
        filters={this.renderFilters()}
        pagination
        onPageChanged={this.onPageChanged}
        currentPage={this.getCurrentPage()}
        actions={this.renderActions()}
        manualPagination
      />
    );
  }
}
const mapStateToProps = (state) => ({
  auth: state.auth,
  clientsList: state.clients.list,
  interventions: state.interventions,
  interventionsList: state.interventions.list,
  users: state.users,
  userProfile: state.auth.userProfile,
  listFilters: state.interventions.filters,
  filtersInProgress: state.interventions.filtersInProgress,
  count: state.interventions.count,
  countInProgress: state.interventions.countInProgress,
  clientSideFiltering: false,
});

const mapDispatchToProps = (dispatch) => ({
  getInterventionList: (filters = {}) => dispatch(getInterventionList(filters)),
  getClientsList: () => dispatch(getClientsList()),
  getUsersList: () => dispatch(getUsersList()),
  getFilters: (filters = {}) => dispatch(getInterventionListFilters(filters)),
  getCount: (filters = {}) => dispatch(getInterventionListCount(filters)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(InterventionListAll);
